<template>
    <app-layout>
        <template #header> </template>

        <jet-dialog-modal :show="showDeleteModal" @close="toggleConfirmModal()">
            <template #content>
                Are you sure you want to
                {{ filter.status === "deleted" ? "recover" : "delete" }}
                {{ checkedAssets.length }} assets?
            </template>

            <template #footer>
                <div class="space-x-4">
                    <jet-secondary-button @click="toggleConfirmModal()">
                        Cancel
                    </jet-secondary-button>

                    <jet-button @click="confirm()"> Confirm </jet-button>
                </div>
            </template>
        </jet-dialog-modal>

        <div class="py-12">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <h1 class="text-2xl font-bold mb-4">Image Library Assets</h1>

                <drag-and-drop-image v-on:uploadComplete="updateAssets" />

                <div class="text-xl font-bold mb-4">Uploaded assets</div>

                <div class="flex">
                    <button
                        @click="updateStatus(statusItem.key)"
                        v-for="statusItem in statusList"
                        :key="statusItem.key"
                        class="p-3"
                        :class="{
                            'bg-white font-bold border-b-2 border-black':
                                statusItem.key === filter.status,
                        }"
                    >
                        {{ statusItem.name }}
                    </button>
                </div>

                <div class="flex justify-between border-b border-gray-300">
                    <div class="flex pt-6 pb-4 justify-center items-center">
                        <div class="mr-4">File type:</div>
                        <div
                            class="
                                space-y-4
                                sm:flex
                                sm:items-center
                                sm:space-y-0
                                sm:space-x-4
                            "
                        >
                            <div
                                v-for="fileTypeItem in fileTypeList"
                                :key="fileTypeItem.key"
                                class="flex items-center"
                            >
                                <div
                                    class="flex items-center pl-3"
                                    :class="{
                                        'border-2 border-black':
                                            fileTypeItem.key ===
                                            filter.fileType,
                                        'border border-gray-600':
                                            fileTypeItem.key !==
                                            filter.fileType,
                                    }"
                                >
                                    <input
                                        type="radio"
                                        :id="fileTypeItem.key"
                                        :value="fileTypeItem.key"
                                        @change="updateFilterType"
                                        v-model="filter.fileType"
                                        class="
                                            focus:ring-black
                                            h-4
                                            w-4
                                            text-black
                                            border-2 border-black
                                        "
                                    />
                                    <label
                                        :for="fileTypeItem.key"
                                        class="
                                            ml-3
                                            block
                                            font-medium
                                            text-black
                                            py-2
                                            pr-3
                                        "
                                        :class="{
                                            'font-bold':
                                                fileTypeItem.key ===
                                                filter.fileType,
                                        }"
                                    >
                                        {{ fileTypeItem.name }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex items-center">
                        <div class="mr-4">Sort By:</div>
                        <select
                            id="sortBy"
                            name="sortBy"
                            v-model="filter.sortBy"
                            @change="updateSortBy"
                        >
                            <option
                                v-for="sortItem in sortByList"
                                :key="sortItem.key"
                                :value="sortItem.key"
                            >
                                {{ sortItem.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <div
                    v-if="meta?.current_page"
                    class="my-6 font-bold flex justify-between"
                >
                    <div>
                        {{ meta?.total }} items found
                        <span
                            v-if="filter.status === 'deleted'"
                            class="text-gray-500"
                        >
                            (Asset can be recovered within 30 days upon
                            deletion)
                        </span>
                    </div>
                    <button
                        class="
                            p-2
                            px-6
                            bg-gray-800
                            text-white
                            flex
                            gap-4
                            justify-center
                            items-center
                        "
                        :class="checkedAssets.length <= 0 ? 'opacity-50' : ''"
                        @click="toggleConfirmModal"
                        :disabled="checkedAssets.length <= 0"
                    >
                        <img
                            class="w-5"
                            src="../../../img/trash-icon-white.svg"
                            alt="delete"
                        />
                        {{ filter.status === "deleted" ? "Recover" : "Delete" }}
                        {{
                            checkedAssets.length > 0 ? checkedAssets.length : ""
                        }}
                    </button>
                </div>
                <div v-else class="my-6 font-bold">-</div>

                <div :class="meta?.current_page ? '' : 'min-h-screen'">
                    <table id="myTable">
                        <tr class="header">
                            <th id="check-delete">
                                <label class="p-3" for="delete-all"
                                    ><input
                                        type="checkbox"
                                        id="delete-all"
                                        v-model="checkAll"
                                /></label>
                            </th>
                            <th id="title">Title</th>
                            <th id="filetype">File type</th>
                            <th id="uploaded-date">Uploaded date</th>
                            <th id="copyright-expires">Copyright expires</th>
                            <th id="asset-owner">Asset owner</th>
                            <th id="asset-status">Asset status</th>
                            <th id="actions"></th>
                        </tr>
                        <tr v-for="asset in assets" :key="asset.id">
                            <td>
                                <label class="p-3" :for="'delete-' + asset.id"
                                    ><input
                                        type="checkbox"
                                        :id="'delete-' + asset.id"
                                        :value="asset.id"
                                        v-model="checkedAssets"
                                /></label>
                            </td>
                            <td>{{ asset.title }}</td>
                            <td>{{ asset.type }}</td>
                            <td>{{ asset.created_at_display }}</td>
                            <td>{{ asset.copyright_expires_at_display }}</td>
                            <td>
                                {{ getAssetOwnerDisplay(asset.asset_owners) }}
                            </td>
                            <td :class="getColor(asset)">
                                {{ asset.status_display }}
                            </td>
                            <td>
                                <a
                                    target="_blank"
                                    class="underline"
                                    :href="'/assets/' + asset.id"
                                    >Edit</a
                                >
                            </td>
                        </tr>
                    </table>

                    <div v-if="meta?.current_page">
                        <div
                            class="empty-user text-center mt-5"
                            v-if="assets < 1"
                        >
                            <b>No files found</b>
                        </div>
                    </div>
                    <div v-else>
                        <div class="empty-user text-center mt-5">
                            <b>Loading...</b>
                        </div>
                    </div>
                </div>
            </div>

            <paginator :paginator="meta" v-on:pageclick="updatePage" />
        </div>
    </app-layout>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import Paginator from "@/components/Paginator";
import DragAndDropImage from "@/Pages/Asset/DragAndDropImage";
import JetDialogModal from "@/Jetstream/DialogModal";
import JetButton from "@/Jetstream/Button";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";

// meta?.current_page determines if it is loading

export default {
    components: {
        AppLayout,
        Paginator,
        DragAndDropImage,
        JetDialogModal,
        JetSecondaryButton,
        JetButton,
    },
    data() {
        return {
            assets: [],
            links: {},
            meta: {},
            filesUploading: [],
            filter: {
                sortBy: "date-uploaded-new-to-old",
                fileType: "all",
                status: "all",
                page: 1,
            },
            checkAll: false,
            checkedAssets: [],
            showDeleteModal: false,
            queryString: "",
            sortByList: [
                {
                    key: "date-uploaded-new-to-old",
                    name: "Date uploaded (new to old)",
                },
                {
                    key: "date-uploaded-old-to-new",
                    name: "Date uploaded (old to new)",
                },
                {
                    key: "expiring-soon",
                    name: "Expiring soon",
                },
                {
                    key: "status-not-published",
                    name: "Status (Not Published)",
                },
                {
                    key: "status-published",
                    name: "Status (Published)",
                },
            ],
            fileTypeList: [
                {
                    key: "all",
                    name: "All files",
                },
                {
                    key: "illustrations",
                    name: "Illustrations only",
                },
                {
                    key: "photos",
                    name: "Photos only",
                },
            ],
            statusList: [
                {
                    key: "all",
                    name: "All",
                },
                {
                    key: "expired",
                    name: "Expired",
                },
                {
                    key: "deleted",
                    name: "Deleted",
                },
            ],
        };
    },
    methods: {
        getAssets(queryString) {
            // trigger "loading" state
            this.assets = [];
            this.links = {};
            this.meta = {};
            axios.get(`/api/assets${queryString}`).then((res) => {
                // after deleting items, if there are no result in the current page, reset to page 1
                if (this.filter.page > 1 && res.data.data.length === 0) {
                    this.filter.page = 1;
                    this.updateAssets();
                    return;
                }

                this.assets = res.data.data;
                this.links = res.data.links;
                this.meta = res.data.meta;
            });
        },
        updateAssets() {
            let searchParams = new URLSearchParams();
            if (this.filter.sortBy) {
                searchParams.set("sortBy", this.filter.sortBy);
            }
            if (this.filter.fileType) {
                searchParams.set("fileType", this.filter.fileType);
            }
            if (this.filter.status) {
                searchParams.set("status", this.filter.status);
            }
            if (this.filter.page) {
                searchParams.set("page", this.filter.page);
            }
            this.getAssets("?" + searchParams.toString());
        },
        updateFilterType() {
            this.filter.page = 1;
            this.checkedAssets = [];
            this.checkAll = false;
            this.updateAssets();
        },
        updateStatus(status) {
            this.filter.status = status;
            this.checkedAssets = [];
            this.checkAll = false;
            this.filter.page = 1;
            this.updateAssets();
        },
        updateSortBy() {
            this.checkedAssets = [];
            this.checkAll = false;
            this.filter.page = 1;
            this.updateAssets();
        },
        updatePage(pageUrl) {
            let searchParams = new URLSearchParams(pageUrl);
            this.filter.page = searchParams.get("page");
            this.updateAssets();
        },
        getAssetOwnerDisplay(assetOwner) {
            if (!assetOwner || assetOwner.length < 1) {
                return "";
            }

            let owners = assetOwner.map((owner) => {
                return owner.name;
            });

            return owners.join(", ");
        },
        getColor(asset) {
            if (asset.status === "draft") {
                if (asset.publish_at) {
                    return "text-yellow-500";
                }
                return "text-red-400";
            }
            if (asset.status === "published") {
                return "text-green-400";
            }

            return "";
        },
        async confirm() {
            let url =
                this.filter.status === "deleted"
                    ? "/api/assets/restore-batch"
                    : "/api/assets/delete-batch";

            let response = await axios
                .post(url, { data: this.checkedAssets })
                .then((res) => {
                    this.checkedAssets = [];
                    this.checkAll = false;
                    this.toggleConfirmModal();
                    this.updateAssets();
                });
        },
        toggleConfirmModal() {
            this.showDeleteModal = !this.showDeleteModal;
        },
    },
    watch: {
        checkAll: function (value) {
            if (value) {
                let assetsId = this.assets.map((asset) => asset.id);
                this.checkedAssets = assetsId;
            } else {
                this.checkedAssets = [];
            }
        },
    },
    mounted() {
        this.updateAssets();
    },
};
</script>

<style scoped>
#myTable {
    border-collapse: collapse; /* Collapse borders */
    width: 100%; /* Full-width */
    border: 1px solid #ddd; /* Add a grey border */
    font-size: 18px; /* Increase font-size */
}

#myTable th,
#myTable td {
    text-align: left; /* Left-align text */
    padding: 12px; /* Add padding */
}

#myTable tr {
    /* Add a bottom border to all table rows */
    border-bottom: 1px solid #ddd;
}

#myTable tr.header,
#myTable tr:hover {
    /* Add a grey background color to the table header and on hover */
    background-color: #f1f1f1;
}
</style>
